/**
 * Shared registration hooks + call infra used by ≥2 apps.
 *
 * Carved out of the admin `features/registrations/hooks.ts`: the reference-data
 * and courier-create hooks are consumed by both the admin RegistrationsPage and
 * the staff panel wizards (AddressFields, OwnerPicker, ComercioFields,
 * NewShopWizard, CreateCourierForm). The admin-only hooks (pending-docs polling,
 * document review, merchant creation) stay in admin and import the infra
 * (`registrationsKeys`, `callUntyped*`, `buildQueryString`, `UserPage`) from here.
 *
 * Extended admin DTOs and `/admin/users` aren't in the generated OpenAPI surface
 * yet — those paths go through `api.GET/POST` with a narrow `as never` cast.
 */
import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api/client';
import { ApiError } from '../api/errors';
import type { components } from '../api/generated/api';
import type { CreateCourierPayload } from './types';

// Re-export so `./hooks` remains the one-stop import for the courier-create hook
// + its payload type (mirrors the original admin hooks.ts surface).
export type { CreateCourierPayload } from './types';

type UserResponse = components['schemas']['UserResponse'];
type CourierResponse = components['schemas']['CourierResponse'];
type CategoryResponse = components['schemas']['CategoryResponse'];
type ViaCepResponse = components['schemas']['ViaCepResponse'];

export interface UserPage {
  content?: UserResponse[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  page?: number;
}

/* ── Query keys ────────────────────────────────────────────────────── */

export const registrationsKeys = {
  all: ['registrations'] as const,
  pendingDocs: (size: number) => ['registrations', 'pending', size] as const,
  pendingCount: () => ['registrations', 'pending', 'count'] as const,
  merchantOwners: () => ['registrations', 'merchant-owners'] as const,
  categories: () => ['registrations', 'categories'] as const,
  documentUrl: (userId: string, type: string) =>
    ['registrations', 'document-url', userId, type] as const,
};

/* ── Internal call helpers (shared with the admin-only hooks) ───────── */

export const buildQueryString = (params: Record<string, unknown>): string => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  });
  return usp.toString() ? `?${usp.toString()}` : '';
};

/**
 * GETs an untyped admin path. Wraps openapi-fetch's typed signature with a
 * narrow `as never` to keep the rest of the call sites strict.
 *
 * TODO: remove once `/admin/users` endpoints land in the OpenAPI doc.
 */
export async function callUntypedGet<T>(path: string): Promise<T> {
  const result = await api.GET(path as never, {} as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
}

/**
 * POSTs an untyped admin path with a JSON body.
 *
 * TODO: remove once the extended `/admin/couriers`, `/admin/merchants` and
 * `/admin/merchants/for-owner` schemas are emitted in api.d.ts.
 */
export async function callUntypedPost<T>(path: string, body: unknown): Promise<T> {
  const result = await api.POST(path as never, { body } as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
}

/* ── Reference data (categories, owners, address lookup) ────────────── */

/**
 * Segmento da LOJA — por isso `type: 'MERCHANT'`.
 *
 * Sem o filtro o endpoint devolve também as categorias de PRODUTO (Comida,
 * Bebida, Sobremesa, Lanche, Porção — seed V3_1), e os dois formulários que
 * consomem este hook (wizard do staff e CreateMerchantForm do admin) as
 * renderiam como opção de segmento. Escolher "Comida" mandaria um categoryId
 * de PRODUCT e o backend devolve 422 INVALID_CATEGORY_TYPE, travando a criação
 * da loja. Antes da BACK-200 passava batido porque só se checava existência.
 */
export function useActiveCategories(): UseQueryResult<CategoryResponse[], Error> {
  return useQuery<CategoryResponse[], Error>({
    queryKey: registrationsKeys.categories(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/public/categories', {
        params: { query: { type: 'MERCHANT' } },
      });
      if (error) throw error as Error;
      return (data ?? []).filter((c) => c.isActive !== false);
    },
  });
}

export function useExistingMerchantOwners(): UseQueryResult<UserResponse[], Error> {
  return useQuery<UserResponse[], Error>({
    queryKey: registrationsKeys.merchantOwners(),
    queryFn: async () => {
      // TODO: drop cast once /admin/users is in the OpenAPI doc.
      const page = await callUntypedGet<UserPage>(
        `/api/v1/admin/users${buildQueryString({ role: 'MERCHANT', isActive: true, size: 500 })}`,
      );
      return page.content ?? [];
    },
  });
}

export interface UseAddressLookupResult {
  lookup: (zipCode: string) => Promise<ViaCepResponse>;
}

/**
 * Public CEP lookup hook. Uses the typed endpoint — no cast required.
 *
 * Why a function (not a query): the lookup is triggered explicitly on the
 * "Buscar" click, so the on-demand promise pattern matches the UX.
 */
export function useAddressLookup(): UseAddressLookupResult {
  const lookup = useCallback(async (zipCode: string): Promise<ViaCepResponse> => {
    const { data, error } = await api.GET('/api/v1/public/address/lookup', {
      params: { query: { zipCode } },
    });
    if (error) throw error as Error;
    if (!data || data.erro === true) {
      throw new ApiError(404, { code: 'ZIP_CODE_NOT_FOUND', message: 'CEP não encontrado' }, 'CEP não encontrado');
    }
    return data;
  }, []);
  return { lookup };
}

/* ── Create mutation (courier) ──────────────────────────────────────── */

export function useCreateCourierRegistration(): UseMutationResult<
  CourierResponse,
  Error,
  CreateCourierPayload
> {
  const queryClient = useQueryClient();
  return useMutation<CourierResponse, Error, CreateCourierPayload>({
    mutationFn: (body) =>
      // TODO: drop cast once the extended AdminCreateCourierRequest is in the OpenAPI doc.
      callUntypedPost<CourierResponse>('/api/v1/admin/couriers', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationsKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['couriers'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
